'use client';

import { useState } from 'react';
import classes from './RichBlocks.module.scss';
import { IconChevronDown, IconMessageCircleQuestion } from '@tabler/icons-react';
import { Text } from '@mantine/core';
import type { RichBlock } from '@/src/types/RichBlock';
import { ConfluenceTemplateBlock } from './ConfluenceTemplateBlock';
import { QuizBlock } from './QuizBlock';
import { RichBlocksProvider } from './RichBlocksContext';

function maxValue(values: number[]): number {
  return values.length ? Math.max(...values) : 0;
}

function ChartBlock({ block }: { block: RichBlock & { type: 'chart' } }) {
  const { chartType, title, labels, values, caption } = block;
  const safeValues = values.map((v) => (typeof v === 'number' && !Number.isNaN(v) ? v : 0));
  const total = safeValues.reduce((a, b) => a + b, 0);
  const max = maxValue(safeValues) || 1;

  return (
    <figure className={classes.richFigure}>
      {title && <figcaption className={classes.richCaptionTitle}>{title}</figcaption>}
      <div className={classes.chartWrap}>
        {chartType === 'pie' ? (
          <svg
            viewBox="-1.2 -1.2 2.4 2.4"
            className={classes.pieSvg}
            role="img"
            aria-label={title || 'Chart'}
          >
            {safeValues.map((value, i) => {
              const previous = safeValues.slice(0, i).reduce((a, b) => a + b, 0);
              const startAngle = (previous / total) * Math.PI * 2 - Math.PI / 2;
              const endAngle = ((previous + value) / total) * Math.PI * 2 - Math.PI / 2;
              const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
              const x1 = Math.cos(startAngle);
              const y1 = Math.sin(startAngle);
              const x2 = Math.cos(endAngle);
              const y2 = Math.sin(endAngle);
              const colors = ['#0ea5e9', '#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
              return (
                <g key={i}>
                  <path
                    d={`M 0 0 L ${x1} ${y1} A 1 1 0 ${largeArc} 1 ${x2} ${y2} Z`}
                    fill={colors[i % colors.length]}
                    stroke="#fff"
                    strokeWidth={0.03}
                  />
                </g>
              );
            })}
          </svg>
        ) : (
          <svg
            className={classes.barSvg}
            viewBox={`0 0 ${Math.max(labels.length * 80, 320)} ${chartType === 'line' ? 180 : 200}`}
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label={title || 'Chart'}
          >
            <rect
              x={40}
              y={20}
              width={Math.max(labels.length * 80, 240)}
              height={140}
              fill="transparent"
            />
            {Array.from({ length: 5 }).map((_, i) => {
              const y = 20 + (140 / 5) * i;
              const label = Math.round(max - (max / 5) * i);
              return (
                <g key={i}>
                  <line
                    x1={40}
                    y1={y}
                    x2={Math.max(labels.length * 80, 280)}
                    y2={y}
                    stroke="#e2e8f0"
                    strokeWidth={1}
                  />
                  <text x={32} y={y + 4} textAnchor="end" fontSize={10} fill="#64748b">
                    {label}
                  </text>
                </g>
              );
            })}
            {chartType === 'line' ? (
              <polyline
                fill="none"
                stroke="#0ea5e9"
                strokeWidth={3}
                points={safeValues
                  .map((v, i) => {
                    const x = 40 + (i + 0.5) * (Math.max(labels.length * 80, 240) / labels.length);
                    const y = 160 - (v / max) * 140;
                    return `${x},${y}`;
                  })
                  .join(' ')}
              />
            ) : (
              safeValues.map((v, i) => {
                const barWidth = (Math.max(labels.length * 80, 240) / labels.length) * 0.5;
                const x =
                  40 + i * (Math.max(labels.length * 80, 240) / labels.length) + barWidth * 0.5;
                const height = (v / max) * 140;
                const y = 160 - height;
                const colors = ['#0ea5e9', '#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
                return (
                  <rect
                    key={i}
                    x={x}
                    y={y}
                    width={barWidth}
                    height={height}
                    fill={colors[i % colors.length]}
                    rx={4}
                  />
                );
              })
            )}
            {labels.map((label, i) => {
              const x = 40 + (i + 0.5) * (Math.max(labels.length * 80, 240) / labels.length);
              return (
                <text key={i} x={x} y={176} textAnchor="middle" fontSize={11} fill="#334155">
                  {label}
                </text>
              );
            })}
          </svg>
        )}
      </div>
      {caption && <figcaption className={classes.richCaption}>{caption}</figcaption>}
    </figure>
  );
}

function ChecklistBlock({ block }: { block: RichBlock & { type: 'checklist' } }) {
  return (
    <div className={classes.richBlock}>
      {block.title && <div className={classes.richBlockTitle}>{block.title}</div>}
      <ul className={classes.checklist}>
        {block.items.map((item, i) => (
          <li key={i} className={classes.checklistItem}>
            <span className={classes.checkBox}>{item.checked ? '☑' : '☐'}</span>
            <span>{item.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ComparisonBlock({ block }: { block: RichBlock & { type: 'comparison' } }) {
  const { title, headers, rows, caption } = block;
  return (
    <figure className={classes.richFigure}>
      {title && <figcaption className={classes.richCaptionTitle}>{title}</figcaption>}
      <div className={classes.tableWrap}>
        <table className={classes.comparisonTable}>
          <thead>
            <tr>
              {headers.map((h, i) => (
                <th key={i}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td key={ci}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {caption && <figcaption className={classes.richCaption}>{caption}</figcaption>}
    </figure>
  );
}

function ExpandableBlock({
  block,
  number,
}: {
  block: RichBlock & { type: 'expandable' };
  number: number;
}) {
  const [open, setOpen] = useState(false);
  const formattedNumber = String(number).padStart(2, '0');

  return (
    <div className={`${classes.richBlock} ${classes.expandableBlock}`}>
      <button
        type="button"
        className={`${classes.expandToggle} ${open ? classes.expandToggleOpen : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className={classes.expandNumber} aria-hidden="true">
          {formattedNumber}
        </span>
        <span className={classes.expandTitle}>{block.title}</span>
        <span className={classes.expandIcon} aria-hidden="true">
          <IconChevronDown size={22} />
        </span>
      </button>
      <div className={`${classes.expandContent} ${open ? classes.expandContentOpen : ''}`}>
        <div className={classes.expandContentInner}>
          <div className={classes.expandHeaderLine} aria-hidden="true" />
          <Text className={classes.expandText}>
            <span className={classes.expandLeadIcon} aria-hidden="true">
              <IconMessageCircleQuestion size={18} />
            </span>
            {block.content}
          </Text>
        </div>
      </div>
    </div>
  );
}

function ImagePlaceholderBlock({ block }: { block: RichBlock & { type: 'imagePlaceholder' } }) {
  return (
    <figure className={classes.richFigure}>
      <figcaption className={classes.richCaptionTitle}>
        <span aria-hidden>🖼</span> {block.prompt}
      </figcaption>
      {block.caption && <figcaption className={classes.richCaption}>{block.caption}</figcaption>}
      <div className={classes.placeholderBox}>
        <Text size="sm" c="dimmed" ta="center">
          {block.caption || 'Image placeholder — upload or generate an image for this section'}
        </Text>
      </div>
    </figure>
  );
}

import MenuLink from '@/src/components/MenuLink/MenuLink';

function RichTextBlock({ block }: { block: RichBlock & { type: 'richText' } }) {
  return (
    <div
      className={classes.richTextBlock}
      style={{ backgroundColor: block.backgroundColor || '#ffffff' }}
    >
      {block.title && <div className={classes.richBlockTitle}>{block.title}</div>}
      <div
        className={classes.richTextContent}
        dangerouslySetInnerHTML={{ __html: block.content }}
      />
      {block.linkUrl && block.linkLabel && (
        <div className={classes.richTextLinkRow}>
          <MenuLink href={block.linkUrl} className={classes.richTextLink}>
            {block.linkLabel}
          </MenuLink>
        </div>
      )}
    </div>
  );
}

export function RichBlocksRenderer({ blocks }: { blocks?: RichBlock[] }) {
  if (!blocks || blocks.length === 0) {
    return null;
  }

  const hasQuiz = blocks.some((b) => b.type === 'quiz');
  let faqNumber = 0;

  return (
    <RichBlocksProvider hasQuiz={hasQuiz}>
      <div className={classes.richBlocks}>
        {blocks.map((block, i) => {
          switch (block.type) {
            case 'chart':
              return <ChartBlock key={block.id || i} block={block} />;
            case 'checklist':
              return <ChecklistBlock key={block.id || i} block={block} />;
            case 'comparison':
              return <ComparisonBlock key={block.id || i} block={block} />;
            case 'expandable':
              faqNumber += 1;
              return <ExpandableBlock key={block.id || i} block={block} number={faqNumber} />;
            case 'richText':
              return (
                <RichTextBlock
                  key={block.id || i}
                  block={block as RichBlock & { type: 'richText' }}
                />
              );
            case 'imagePlaceholder':
              return <ImagePlaceholderBlock key={block.id || i} block={block} />;
            case 'quiz':
              return (
                <QuizBlock
                  key={block.id || i}
                  title={block.title}
                  questions={block.questions}
                  passingScore={block.passingScore}
                />
              );
            case 'confluenceTemplate':
              return <ConfluenceTemplateBlock key={block.id || i} block={block} />;
            // certificate rendering intentionally disabled — keep type for backward compatibility
            case 'certificate':
              return null;
            default:
              return null;
          }
        })}
      </div>
    </RichBlocksProvider>
  );
}
